import L from 'leaflet';
import iconRetinaUrl from 'leaflet/dist/images/marker-icon-2x.png';
import iconUrl from 'leaflet/dist/images/marker-icon.png';
import shadowUrl from 'leaflet/dist/images/marker-shadow.png';
import { useEffect, useRef, useState } from 'react';
import { FormField, Input } from './FormField.js';

// Leaflet's default icon resolves its images relative to the page URL under
// Vite bundling (dist/images is never served) → broken pin. Point it at the
// bundled assets instead; deleting _getIconUrl forces the merged options.
// biome-ignore lint/performance/noDelete: leaflet's documented workaround
delete (L.Icon.Default.prototype as unknown as { _getIconUrl?: unknown })._getIconUrl;
L.Icon.Default.mergeOptions({ iconRetinaUrl, iconUrl, shadowUrl });

export interface Coordinates {
  latitude: number;
  longitude: number;
}

interface LocationMapProps {
  value: Coordinates | null;
  onChange?: (coords: Coordinates) => void;
  /** Interactive = draggable pin + address search (create/edit). Read-only omits both (US3). */
  interactive?: boolean;
}

// KL city centre — sensible default view when a location has no point yet.
const DEFAULT_CENTER: [number, number] = [3.139, 101.6869];
const NOMINATIM_URL = 'https://nominatim.openstreetmap.org/search';
const SEARCH_DEBOUNCE_MS = 600; // OSM usage policy: ≤ ~1 request/second
const MIN_QUERY_LENGTH = 3;

interface GeocodeResult {
  lat: number;
  lon: number;
  label: string;
}

export function LocationMap({ value, onChange, interactive = true }: LocationMapProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<L.Map | undefined>(undefined);
  const markerRef = useRef<L.Marker | undefined>(undefined);
  const searchRef = useRef<HTMLDivElement>(null);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<GeocodeResult[]>([]);
  const [searchFailed, setSearchFailed] = useState(false);
  const [searched, setSearched] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  // Picking a result writes its label into the box; that edit must not re-search.
  const skipNextSearchRef = useRef(false);

  function emit(marker: L.Marker) {
    const { lat, lng } = marker.getLatLng();
    onChange?.({ latitude: lat, longitude: lng });
  }

  function ensureMarker(map: L.Map, ll: [number, number]): L.Marker {
    if (markerRef.current) {
      markerRef.current.setLatLng(ll);
      return markerRef.current;
    }
    const marker = L.marker(ll, { draggable: interactive }).addTo(map);
    if (interactive) marker.on('dragend', () => emit(marker));
    markerRef.current = marker;
    return marker;
  }

  // Initialise the map when its container first exists. The container can
  // appear AFTER mount: a read-only map starts in the empty state until the
  // location's coordinates load async (Gate B Q11), so this effect runs on
  // every render and the ref guards make it init-once.
  useEffect(() => {
    if (!containerRef.current || mapRef.current) return;
    const center: [number, number] = value ? [value.latitude, value.longitude] : DEFAULT_CENTER;
    const map = L.map(containerRef.current).setView(center, value ? 13 : 6);
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '© OpenStreetMap contributors',
      maxZoom: 19,
    }).addTo(map);
    mapRef.current = map;
    if (value) ensureMarker(map, [value.latitude, value.longitude]);
  });

  // Tear the map down on unmount only (the init effect above has no deps, so
  // its own cleanup would run — and destroy the map — after every render).
  useEffect(
    () => () => {
      mapRef.current?.remove();
      mapRef.current = undefined;
      markerRef.current = undefined;
    },
    [],
  );

  // Reflect external coordinate changes (prefill, search selection) onto the map.
  // biome-ignore lint/correctness/useExhaustiveDependencies: only react to value
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !value) return;
    const ll: [number, number] = [value.latitude, value.longitude];
    ensureMarker(map, ll);
    map.setView(ll, 13);
  }, [value]);

  // Debounced Nominatim address search (interactive only).
  useEffect(() => {
    if (!interactive) return;
    if (skipNextSearchRef.current) {
      skipNextSearchRef.current = false;
      return;
    }
    if (query.trim().length < MIN_QUERY_LENGTH) {
      setResults([]);
      setSearched(false);
      return;
    }
    const timer = setTimeout(async () => {
      try {
        const url = `${NOMINATIM_URL}?format=jsonv2&countrycodes=my&limit=5&q=${encodeURIComponent(query)}`;
        const res = await fetch(url);
        if (!res.ok) throw new Error('geocode request failed');
        const data = (await res.json()) as Array<{
          lat: string;
          lon: string;
          display_name: string;
        }>;
        setResults(
          data.map((d) => ({ lat: Number(d.lat), lon: Number(d.lon), label: d.display_name })),
        );
        setActiveIndex(-1);
        setSearchFailed(false);
        setSearched(true);
      } catch {
        setSearchFailed(true);
        setResults([]);
        setSearched(false);
      }
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [query, interactive]);

  // Clicking anywhere outside the search box dismisses an open result list.
  useEffect(() => {
    if (results.length === 0) return;
    function onDocumentMouseDown(e: MouseEvent) {
      if (!searchRef.current?.contains(e.target as Node)) closeResults();
    }
    document.addEventListener('mousedown', onDocumentMouseDown);
    return () => document.removeEventListener('mousedown', onDocumentMouseDown);
  }, [results.length]);

  function closeResults() {
    setResults([]);
    setActiveIndex(-1);
    setSearched(false);
  }

  function selectResult(r: GeocodeResult) {
    onChange?.({ latitude: r.lat, longitude: r.lon });
    skipNextSearchRef.current = true;
    closeResults();
    setQuery(r.label);
  }

  function onSearchKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Enter') {
      // The map lives inside the location <form> — a bare Enter would implicitly
      // submit it and save the location mid-edit. Enter picks a result instead.
      e.preventDefault();
      const pick = results[activeIndex] ?? results[0];
      if (pick) selectResult(pick);
      return;
    }
    if (e.key === 'Escape') {
      closeResults();
      return;
    }
    if (e.key === 'ArrowDown' && results.length > 0) {
      e.preventDefault();
      setActiveIndex((i) => Math.min(i + 1, results.length - 1));
      return;
    }
    if (e.key === 'ArrowUp' && results.length > 0) {
      e.preventDefault();
      setActiveIndex((i) => Math.max(i - 1, 0));
    }
  }

  // US3: a read-only location with no coordinates shows an explicit empty state
  // rather than a misleading default map.
  if (!interactive && !value) {
    return (
      <div
        data-testid="location-map-empty"
        className="flex h-64 w-full items-center justify-center rounded-md border border-dashed border-hairline text-sm text-muted"
      >
        No map position set
      </div>
    );
  }

  return (
    <div className="space-y-2" data-testid="location-map">
      {interactive && (
        // No `name` on the input: the enclosing location form serialises itself
        // with new FormData(form), and a named search box would be PATCHed.
        <FormField label="Search address" name="location-map-search">
          <div className="relative" ref={searchRef}>
            <Input
              type="search"
              id="location-map-search"
              autoComplete="off"
              placeholder="Street, city…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={onSearchKeyDown}
            />
            {searchFailed && (
              <p className="mt-1 text-xs text-semantic-warning-text">
                Address search unavailable — place the pin manually.
              </p>
            )}
            {searched && results.length === 0 && (
              <p className="mt-1 text-xs text-muted">No matches for that address.</p>
            )}
            {results.length > 0 && (
              <ul className="absolute z-[500] mt-1 max-h-48 w-full overflow-auto rounded-md border border-hairline bg-canvas shadow-lg">
                {results.map((r, i) => (
                  <li key={`${r.lat},${r.lon}`}>
                    <button
                      type="button"
                      onClick={() => selectResult(r)}
                      onMouseEnter={() => setActiveIndex(i)}
                      className={[
                        'block w-full px-3 py-2 text-left text-sm text-ink hover:bg-surface',
                        i === activeIndex ? 'bg-surface' : '',
                      ].join(' ')}
                    >
                      {r.label}
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </FormField>
      )}
      {/* #1427: `isolate` makes the map container its own stacking context, so Leaflet's panes and controls (z-index
          400–1000) stay inside it: the page's sticky header and the address results (z-[500]) draw above the map. */}
      <div
        ref={containerRef}
        data-testid="location-map-canvas"
        className="isolate h-64 w-full rounded-md border border-hairline"
      />
    </div>
  );
}
